import { useState, useRef, useEffect, type ReactNode } from "react";
import { useRouter } from "next/router";
import {
  List,
  Sun,
  Moon,
  SignOut,
  User,
  Coffee,
  Eye,
  Bell,
  Calculator,
  DotsThreeVertical,
} from "@phosphor-icons/react";
import useSWR from "swr";
import { getAccessToken } from "lib/accessToken";
import parseJwt from "utils/helper/jwtParser";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import dynamic from "next/dynamic";

import Sidebar from "./SideBar";
import BottomDock from "./BottomDock";
import GlobalPomodoro from "./GlobalPomodoro";
import GlobalCalculator from "./GlobalCalculator";
import { useTheme } from "lib/context/ThemeContext";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getUser, logout, isAdmin as getIsAdmin } from "redux/reducers/userSlice";
import { logOut } from "lib/common/helper/login";
import { cn } from "@/lib/utils";
import WithTryLoggingIn from "lib/hoc/withTryLoggingIn";
import { imgUrl, resolveImgUrl } from "utils/constants";
import { notices as noticesUrl } from "lib/common/endpoints/endUser";

const NOTICE_LAST_SEEN_KEY = "clamphook-notices-last-seen";

interface NoticeSummary {
  _id: { $oid: string } | string;
  title: string;
  description?: string;
  createdAt?: number;
}

const noticeId = (n: NoticeSummary) => (typeof n._id === "string" ? n._id : n._id.$oid);

function useNotices() {
  const { data } = useSWR(noticesUrl);
  const notices: NoticeSummary[] = data?.data ?? [];
  const latestCreatedAt = notices.reduce((max, n) => Math.max(max, n.createdAt ?? 0), 0);

  const hasUnread = (() => {
    if (latestCreatedAt === 0) return false;
    if (typeof window === "undefined") return false;
    const lastSeen = Number(localStorage.getItem(NOTICE_LAST_SEEN_KEY) ?? 0);
    return latestCreatedAt > lastSeen;
  })();

  return { notices, hasUnread };
}

function markNoticesSeen() {
  if (typeof window === "undefined") return;
  localStorage.setItem(NOTICE_LAST_SEEN_KEY, String(Math.floor(Date.now() / 1000)));
}

function NoticeDropdown({ notices, hasUnread }: { notices: NoticeSummary[]; hasUnread: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = () => {
    setOpen((v) => !v);
    if (!open) markNoticesSeen();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        className="relative flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        aria-label="Notices"
        title="Notices"
      >
        <Bell size={16} />
        {hasUnread && (
          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-primary" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl z-50 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100">Notices</p>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
              {notices.length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
                  No notices right now.
                </p>
              )}
              {notices.slice(0, 5).map((notice) => (
                <Link
                  key={noticeId(notice)}
                  href={`/notice/${noticeId(notice)}`}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 no-underline transition-colors"
                >
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {notice.title}
                  </p>
                  {notice.description && (
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                      {notice.description}
                    </p>
                  )}
                </Link>
              ))}
            </div>
            <Link
              href="/notice"
              onClick={() => setOpen(false)}
              className="block px-4 py-3 text-center text-xs font-semibold text-primary dark:text-primary-300 hover:bg-gray-50 dark:hover:bg-gray-800 no-underline border-t border-gray-100 dark:border-gray-800"
            >
              View all notices
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ToolsDropdown({
  onPomodoro,
  onCalculator,
}: {
  onPomodoro: () => void;
  onCalculator: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative hidden lg:block">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        aria-label="Study tools"
        title="Study tools"
      >
        <DotsThreeVertical size={16} weight="bold" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-44 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl z-50 overflow-hidden"
          >
            <button
              onClick={() => {
                onPomodoro();
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Coffee size={16} /> Pomodoro
            </button>
            <button
              onClick={() => {
                onCalculator();
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Calculator size={16} /> Calculator
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DashboardLayoutInner({ children }: { children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showProfileList, setShowProfileList] = useState(false);
  const profileListRef = useRef<HTMLDivElement>(null);
  const { notices, hasUnread: hasUnreadNotice } = useNotices();

  useEffect(() => {
    if (!showProfileList) return;
    const handler = (e: MouseEvent) => {
      if (
        profileListRef.current &&
        !profileListRef.current.contains(e.target as Node)
      ) {
        setShowProfileList(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showProfileList]);
  const router = useRouter();
  const { theme, toggleTheme } = useTheme();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const userIsAdmin = useAppSelector(getIsAdmin);
  const [pomVisible, setPomVisible] = useState(false);
  const [calcVisible, setCalcVisible] = useState(false);

  const initials = (user?.fullName || "U")
    .split(" ")
    .map((s: string) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const avatarSrc = user?.userProfilePicture
    ? resolveImgUrl(user.userProfilePicture)
    : user?.userAvatarPicture
      ? `/avatar/${user.userAvatarPicture}`
      : "/img/logo/App_Icon_Primary.png";

  const handleLogout = () => {
    logOut();
    dispatch(logout());
    window.location.href = "/";
  };

  // Decode the current access token once to detect impersonation sessions.
  const impersonationClaims = (() => {
    try {
      const claims = parseJwt(getAccessToken() ?? "");
      if (claims?.isImpersonation) return claims;
    } catch {}
    return null;
  })();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* ── Impersonation banner ── */}
      {impersonationClaims && (
        <div className="fixed top-0 inset-x-0 z-[60] flex items-center justify-center gap-2 bg-amber-400 px-4 py-1.5 text-xs font-semibold text-amber-900">
          <Eye size={13} weight="fill" aria-hidden="true" />
          Viewing as <span className="font-black">{impersonationClaims.sub}</span>
          &nbsp;·&nbsp;Admin: {impersonationClaims.impersonatedByUsername}
          &nbsp;·&nbsp;Session expires in 30 min — read-only, no refresh
        </div>
      )}

      {/* ── Sidebar ── */}
      <Sidebar
        activePath={router.pathname}
        mobileOpen={sidebarOpen}
        onMobileClose={() => setSidebarOpen(false)}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        onLogout={() => setShowLogoutConfirm(true)}
        isAdmin={userIsAdmin}
      />

      {/* ── Main area shifts right to clear the sidebar on desktop ── */}
      <div
        className={cn(
          // overflow-x-clip, not overflow-x-hidden: setting overflow-x to
          // "hidden" forces the browser to also compute overflow-y as
          // "auto" (the two can't be mixed visible/non-visible), which
          // turns this div into its own scroll container -- and the
          // sticky topbar below then sticks relative to that box instead
          // of the real viewport, so it just scrolls away with the page.
          // "clip" isn't part of that pairing rule, so it blocks the
          // horizontal overflow without breaking sticky descendants.
          "flex flex-col min-h-screen overflow-x-clip transition-[padding-left] duration-300 ease-in-out",
          collapsed ? "lg:pl-20" : "lg:pl-64",
          impersonationClaims && "pt-7",
        )}
      >
        {/* ── Top bar ── */}
        <header className="sticky top-0 z-40 flex h-14 items-center gap-2 px-4 sm:px-6 border-b border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm">
          {/* Hamburger (mobile only) */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            aria-label="Open menu"
          >
            <List size={18} />
          </button>

          {/* Brand logo — mobile only */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/img/logo/ClampHook_Logo_Primary.png"
            alt="ClampHook"
            className="lg:hidden h-6 w-auto object-contain ml-2 dark:brightness-0 dark:invert"
          />

          <div className="flex-1" />

          {/* Notices */}
          <NoticeDropdown notices={notices} hasUnread={hasUnreadNotice} />

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/* Pomodoro + Calculator — grouped, desktop only */}
          <ToolsDropdown
            onPomodoro={() => setPomVisible((v) => !v)}
            onCalculator={() => setCalcVisible((v) => !v)}
          />

          {/* User avatar + name — desktop shows name beside avatar */}
          <div ref={profileListRef} className="relative">
            <button
              onClick={() => setShowProfileList((v) => !v)}
              className="flex items-center gap-2 rounded-xl pl-2 pr-1 py-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition select-none"
              title={user?.fullName || "Profile"}
              aria-label="Profile menu"
            >
              <div className="hidden md:flex flex-col items-end justify-center order-2 text-right">
                <span className="text-sm font-semibold text-gray-900 dark:text-white text-right leading-tight max-w-[9rem] truncate">
                  {user?.fullName}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500 leading-tight">
                  @{user?.userData?.username}
                </span>
              </div>
              <div className="order-1 h-9 w-9 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm flex-shrink-0 bg-primary">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={avatarSrc}
                  alt={initials}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      "/img/logo/App_Icon_Primary.png";
                  }}
                />
              </div>
            </button>
            {/* dropdown stays exactly as-is */}

            <AnimatePresence>
              {showProfileList && (
                <motion.div
                  className="absolute right-0 top-11 z-50 w-48 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden"
                  initial={{ opacity: 0, scale: 0.92, y: -8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.92, y: -8 }}
                  transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* User info pill */}
                  <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight m-0">
                      {user?.fullName || "User"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate leading-tight m-0 mt-0.5">
                      @{user?.userData?.username}
                    </p>
                  </div>

                  <Link
                    href="/profile"
                    onClick={() => setShowProfileList(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <User
                      size={15}
                      className="text-gray-400 dark:text-gray-500"
                    />
                    View Profile
                  </Link>

                  <button
                    onClick={() => {
                      setShowProfileList(false);
                      setShowLogoutConfirm(true);
                    }}
                    className="flex w-full items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <SignOut size={15} />
                    Log out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </header>

        {/* ── Page content ── extra bottom padding on mobile for the dock */}
        <main className="flex-1 pb-20 lg:pb-0">{children}</main>
      </div>

      {/* ── Mobile bottom dock — hidden on classroom detail pages ── */}
      {!/^\/classrooms\/[^/]+/.test(router.pathname) && (
        <BottomDock activePath={router.pathname} />
      )}

      {/* Global helpers */}
      <GlobalPomodoro
        visible={pomVisible}
        onClose={() => setPomVisible(false)}
      />
      <GlobalCalculator
        visible={calcVisible}
        onClose={() => setCalcVisible(false)}
      />

      {/* ── Logout confirmation modal ── */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setShowLogoutConfirm(false)}
            style={{
              backgroundColor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(4px)",
            }}
          >
            <motion.div
              className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl p-6"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 mx-auto mb-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  delay: 0.1,
                  duration: 0.4,
                  ease: [0.34, 1.56, 0.64, 1],
                }}
              >
                <SignOut className="text-red-500 dark:text-red-400" size={22} />
              </motion.div>
              <h3 className="text-center text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Log out?
              </h3>
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-6">
                You&apos;ll need to sign in again to access your account.
              </p>
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleLogout}
                  className="flex-1 rounded-xl bg-red-500 hover:bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors"
                >
                  Log out
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const DashboardLayoutWithAuth = WithTryLoggingIn(
  DashboardLayoutInner as React.FC<{ children: ReactNode }>,
);

export function dashboardLayout(page: ReactNode): ReactNode {
  return <DashboardLayoutWithAuth>{page}</DashboardLayoutWithAuth>;
}

export default DashboardLayoutInner;
